import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';

import { TextInputType, NumberValueType } from "../../../libs/data/formControlTypes";

import TooltipComponent from "../../../../ui/tooltip/TooltipComponent";

import "./Input.scss";

const excptIntSymbol = ["e", "E", "+", "-"];
const excptDecimalSymbol = ["e", "E", "+", "-"];

/**
 * A customizable input component that supports various input types and validation.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.type - The type of input field (e.g., text, number).
 * @param {string} [props.name=""] - The name attribute for the input field.
 * @param {string} [props.placeholder=`${name}`] - The placeholder text for the input field.
 * @param {string|number} props.value - The value of the input field.
 * @param {number} [props.min] - The minimum value (for number inputs).
 * @param {number} [props.max] - The maximum value (for number inputs).
 * @param {Function} props.onChange - Callback function when input value changes.
 * @param {Function} [props.onBlur] - Callback function when the input loses focus.
 * @param {number} [props.minLength] - The minimum length of the input.
 * @param {number} [props.maxLength] - The maximum length of the input.
 * @param {string} [props.cssClass="input-field"] - CSS class for styling the input.
 * @param {boolean} [props.allowSpace=true] - Whether to allow spaces in the input.
 * @param {string} [props.valueType] - The type of value (e.g., integer, decimal) for number inputs.
 * @param {boolean} [props.isdisable] - Whether the input is disabled.
 * @param {boolean} [props.isReadOnly] - Whether the input is read-only.
 */
const Input = ({
  id,
  keyId,
  type = TextInputType.TEXT,
  name = "",
  placeholder = `${name}`,
  value,
  min,
  max,
  onChange,
  onBlur,
  minLength,
  maxLength,
  cssClass = "input-field",
  allowSpace = true,
  valueType,
  isdisable,
  isreadonly,
  dataField,
  inputInfoConfig,
}) => {

  const [inputAttributes, setInputAttributes] = useState({});
  const [selectValue, setSelectValue] = useState(value);

  const updateAttributes = () => {
    const newAttribute = { ...inputAttributes };

    if (minLength) {
      newAttribute.minLength = minLength;
    }
    if (maxLength) {
      newAttribute.maxLength = maxLength;
    }

    if (isreadonly) {
      newAttribute.readOnly = isreadonly;
    }

    if (type === TextInputType.NUMBER && valueType === NumberValueType.INT) {
      newAttribute.step = 1;
    }

    setInputAttributes(newAttribute);
  };

  const handleInputChange = (e) => {
    let inputValue = e.target.value;

    if (type === TextInputType.NUMBER && maxLength) {
      inputValue = inputValue.slice(0, maxLength);
      e.target.value = inputValue;
    }
    setSelectValue(inputValue)
    onChange(inputValue);
  };

  const handleBlur = (e) => {
    let value = e.target.value;
    if (type === TextInputType.NUMBER) {
      if (isNaN(value) || !value || value < 0) {
        value = "0";
        setSelectValue(0)
      }
    }
    onBlur?.(dataField, value);
  }

  const handleKeyDown = (e) => {
    if (!allowSpace && e.keyCode === 32) {
      e.preventDefault();
      return;
    }

    if (type === TextInputType.NUMBER && valueType === NumberValueType.INT && excptIntSymbol.includes(e.key)) {
      e.preventDefault();
      return;
    }

    if (type === TextInputType.NUMBER && valueType === NumberValueType.DECIMAL && excptDecimalSymbol.includes(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    updateAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minLength, maxLength, type, valueType]);

  useEffect(() => {
    setSelectValue(value)
  }, [value]);

  return (
    <div className={`input-group ${type === TextInputType.PASSWORD ? 'custom-pass-sec' : ''}`}>
      <input
        id={id}
        key={keyId}
        value={selectValue}
        name={name}
        type={type}
        className={cssClass}
        placeholder={placeholder}
        autoComplete="new-password"
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={(e) => handleBlur(e)}
        disabled={isdisable}
        min={min}
        max={max}
        readOnly={isreadonly}
        {...inputAttributes}
      />
      {inputInfoConfig?.isInfoIcon && (
        <TooltipComponent
          iconName={inputInfoConfig?.iconName}
          message={inputInfoConfig?.message}
        />
      )}
    </div>
  );
};

Input.propTypes = {
  id: PropTypes.string,
  keyId: PropTypes.string,
  type: PropTypes.oneOf([TextInputType.TEXT, TextInputType.EMAIL, TextInputType.PASSWORD, TextInputType.NUMBER]),
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onKeyup: PropTypes.func,
  onBlur: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  cssClass: PropTypes.string,
  allowSpace: PropTypes.bool,
  isdisable: PropTypes.bool,
  isreadonly: PropTypes.bool,
  dataField: PropTypes.string.isRequired,
  valueType: PropTypes.oneOf([NumberValueType.INT, NumberValueType.DECIMAL]),
  inputInfoConfig: PropTypes.shape({
    isInfoIcon: PropTypes.bool,
    iconName: PropTypes.string,
    message: PropTypes.string,
  }),
};

export default Input;
